import { useEffect, useRef } from 'react'

/**
 * Premium Dark Blue Cursor
 * - Smooth follow
 * - Trail
 * - Click burst
 * - Ignite on hover
 * - Magnetic buttons
 * - Desktop only
 */
export default function CustomCursor() {
  const cursorRef = useRef(null)
  const rafRef = useRef(null)
  const timeoutRef = useRef(null)

  const pos = useRef({ x: 0, y: 0 })
  const target = useRef({ x: 0, y: 0 })

  const trailNodes = useRef([])
  const pool = useRef([])

  useEffect(() => {
    const isDesktop = window.matchMedia('(pointer: fine)').matches
    if (!isDesktop) return

    const cursor = cursorRef.current
    if (!cursor) return

    pos.current.x = window.innerWidth / 2
    pos.current.y = window.innerHeight / 2
    target.current.x = pos.current.x
    target.current.y = pos.current.y

    const asElement = (targetNode) => {
      if (targetNode instanceof Element) return targetNode
      if (targetNode?.parentElement instanceof Element) return targetNode.parentElement
      return null
    }

    const setActive = () => {
      cursor.classList.add('active', 'visible')
      clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        cursor.classList.remove('active')
      }, 1000)
    }

    const isInteractive = (targetNode) => {
      const el = asElement(targetNode)
      return el?.closest?.('a,button,input,select,textarea,[role="button"]') || null
    }

    const setIgnite = (targetNode) => {
      if (isInteractive(targetNode)) cursor.classList.add('ignite')
      else cursor.classList.remove('ignite')
    }

    const spawnTrail = (x, y) => {
      let dot = pool.current.pop()
      if (!dot) {
        dot = document.createElement('div')
        dot.className = 'cursor-trail'
        document.body.appendChild(dot)
      }

      dot.style.transform = `translate3d(${x}px, ${y}px, 0)`
      dot.style.opacity = '1'
      dot.dataset.life = '1'
      trailNodes.current.push(dot)

      if (trailNodes.current.length > 18) {
        const old = trailNodes.current.shift()
        if (old) {
          old.style.opacity = '0'
          pool.current.push(old)
        }
      }
    }

    const burst = (x, y) => {
      const ring = document.createElement('div')
      ring.className = 'cursor-burst'
      ring.style.setProperty('--tx', `${x}px`)
      ring.style.setProperty('--ty', `${y}px`)
      ring.style.transform = `translate3d(${x}px, ${y}px, 0)`
      document.body.appendChild(ring)
      setTimeout(() => ring.remove(), 520)
    }

    const applyMagnetic = (e) => {
      const targetEl = asElement(e.target)
      const el = targetEl?.closest?.('a,button')
      if (!el) return

      const rect = el.getBoundingClientRect()
      const x = e.clientX - rect.left - rect.width / 2
      const y = e.clientY - rect.top - rect.height / 2

      el.style.transition = 'transform 0.15s ease'
      el.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`
    }

    const resetMagnetic = (e) => {
      const targetEl = asElement(e.target)
      const el = targetEl?.closest?.('a,button')
      if (!el) return
      el.style.transform = 'translate(0,0)'
    }

    const onMove = (e) => {
      target.current.x = e.clientX
      target.current.y = e.clientY
      setActive()
      setIgnite(e.target)
      spawnTrail(e.clientX, e.clientY)
      applyMagnetic(e)
    }

    const onClick = (e) => {
      burst(e.clientX, e.clientY)
      setActive()
    }

    const animate = () => {
      pos.current.x += (target.current.x - pos.current.x) * 0.18
      pos.current.y += (target.current.y - pos.current.y) * 0.18

      cursor.style.transform = `translate3d(${pos.current.x}px, ${pos.current.y}px, 0)`

      for (let i = trailNodes.current.length - 1; i >= 0; i--) {
        const dot = trailNodes.current[i]
        const life = parseFloat(dot.dataset.life || '1') - 0.065
        dot.dataset.life = life
        dot.style.opacity = Math.max(0, life)
        if (life <= 0) {
          trailNodes.current.splice(i, 1)
          pool.current.push(dot)
        }
      }

      rafRef.current = requestAnimationFrame(animate)
    }

    document.addEventListener('mousemove', onMove, { passive: true })
    document.addEventListener('click', onClick)
    document.addEventListener('mouseleave', resetMagnetic, true)
    document.addEventListener('mouseout', resetMagnetic, true)

    rafRef.current = requestAnimationFrame(animate)

    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('click', onClick)
      document.removeEventListener('mouseleave', resetMagnetic, true)
      document.removeEventListener('mouseout', resetMagnetic, true)

      clearTimeout(timeoutRef.current)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)

      trailNodes.current.forEach((n) => n.remove())
      pool.current.forEach((n) => n.remove())
    }
  }, [])

  return <div ref={cursorRef} className="custom-cursor" />
}
